/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	DEFAULT_BRANDING_COLOR,
	HubErrorBoundary,
	useHubContext,
} from '@atlassian/company-hub-common';

import { readableTextColor } from '../../utils';

const styles = cssMap({
	bannerContainer: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
	},
	textContainer: {
		height: '56px',
		marginTop: token('space.1000'),
		marginBottom: token('space.1000'),
	},
	textContainerWidth: {
		width: '90%',
	},
	bannerText: {
		fontWeight: token('font.weight.bold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '48px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '56px',
	},
	bannerTextTruncation: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		width: '100%',
		textAlign: 'center',
	},
	container: {
		borderRadius: token('radius.small'),
	},
});

const BannerContent = ({ bannerText }: { bannerText?: string }) => {
	di(useHubContext, fg);
	const { branding, layout } = useHubContext();
	const bannerBackgroundColour = branding.colors.primary;
	const title = bannerText ? bannerText : layout?.bannerText;

	return (
		<div
			css={styles.container}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				backgroundColor: bannerBackgroundColour,
			}}
			data-testid="banner"
		>
			<Stack alignInline="center">
				<div css={[styles.textContainer, fg('enable-jsm-in-hubs') && styles.textContainerWidth]}>
					<div
						css={[styles.bannerText, fg('enable-jsm-in-hubs') && styles.bannerTextTruncation]}
						data-testid="banner-text"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
						style={{ color: readableTextColor(bannerBackgroundColour) }}
					>
						{title || ''}
					</div>
				</div>
			</Stack>
		</div>
	);
};

// Fallback banner shown when BannerContent fails to render
const BannerFallback = () => (
	<div
		css={styles.container}
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
			backgroundColor: DEFAULT_BRANDING_COLOR,
		}}
		data-testid="banner-fallback"
	>
		<Stack alignInline="center">
			<div css={styles.textContainer}>
				<div
					css={styles.bannerText}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ color: token('color.text') }}
				/>
			</div>
		</Stack>
	</div>
);

export const Banner = ({ bannerText }: { bannerText?: string }): JSX.Element => {
	return (
		<HubErrorBoundary
			packageName="@atlassian/hub-app-shell"
			id="banner"
			fallback={<BannerFallback />}
		>
			<BannerContent bannerText={bannerText} />
		</HubErrorBoundary>
	);
};

export const withHubBanner = <P extends object>(
	InnerComponent: React.ComponentType<P>,
): ((props: P & { bannerText?: string }) => JSX.Element) => {
	const ComponentWithHubBanner = (props: P & { bannerText?: string }) => {
		const { bannerText, ...rest } = props;

		return (
			<React.Fragment>
				<div css={styles.bannerContainer}>
					<Banner bannerText={bannerText} />
				</div>
				<InnerComponent {...(rest as P)} />
			</React.Fragment>
		);
	};

	return ComponentWithHubBanner;
};
